import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RiArrowRightLine, RiCheckLine } from '@remixicon/react'
import { Button } from '../ui'
import { cx } from '../../lib/utils'
import Logo from '../ui/Logo'

/** The pieces the applicant's page is made of, apart from its state. */

export type WizardStep = 'details' | 'survey' | 'confirm'

const WIZARD: { key: WizardStep; label: string }[] = [
  { key: 'details', label: 'onboardingApply.stepDetails' },
  { key: 'survey', label: 'onboardingApply.stepSurvey' },
  { key: 'confirm', label: 'onboardingApply.stepConfirm' },
]

export function Brand() {
  return (
    <Logo />
  )
}

/** The page around every screen of the application: the gradient, the brand and a column. */
export function ApplyShell({
  children,
  width = 'max-w-2xl',
  aside,
  centered,
}: {
  children: ReactNode
  width?: string
  /** Something small at the right of the brand, hidden on a narrow screen. */
  aside?: ReactNode
  /** Centre the content vertically: the landing and the thank-you, not the wizard. */
  centered?: boolean
}) {
  return (
    <main className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className={cx('mx-auto flex min-h-screen flex-col px-4 py-6', width)}>
        <header className="flex items-center justify-between">
          <Brand />
          {aside && <div className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">{aside}</div>}
        </header>
        <div className={cx('flex flex-1 flex-col', centered ? 'justify-center py-10' : 'pt-8')}>{children}</div>
      </div>
    </main>
  )
}

/** Where the applicant is in the three steps, and what they have already done. */
export function Stepper({ current }: { current: WizardStep }) {
  const { t } = useTranslation()
  const index = WIZARD.findIndex((step) => step.key === current)

  return (
    <>
      <ol className="flex items-center" aria-label={t('onboardingApply.steps')}>
        {WIZARD.map((step, position) => {
          const done = position < index
          const active = position === index
          return (
            <li
              key={step.key}
              aria-current={active ? 'step' : undefined}
              className={cx('flex items-center', position < WIZARD.length - 1 && 'flex-1')}
            >
              <div className="flex items-center gap-3">
                <span
                  className={cx(
                    'flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ring-2 transition',
                    done && 'bg-primary-600 text-white ring-primary-600',
                    active && 'bg-white text-primary-700 ring-primary-600 dark:bg-gray-950 dark:text-primary-300',
                    !done && !active && 'bg-white text-gray-500 ring-gray-300 dark:bg-gray-950 dark:text-gray-400 dark:ring-gray-700',
                  )}
                >
                  {done ? <RiCheckLine className="size-5" aria-hidden="true" /> : position + 1}
                </span>
                {/* On a narrow screen the labels are for assistive technology and the current one is written below. */}
                <span
                  className={cx(
                    'sr-only sm:not-sr-only sm:text-sm sm:font-medium',
                    active ? 'text-gray-900 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400',
                  )}
                >
                  {t(step.label)}
                  {done && ` (${t('onboardingApply.stepDone')})`}
                </span>
              </div>
              {position < WIZARD.length - 1 && (
                <span
                  className={cx(
                    'mx-3 h-px flex-1 transition sm:mx-4',
                    done ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-800',
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          )
        })}
      </ol>
      <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-50 sm:hidden" aria-hidden="true">
        {t(WIZARD[index].label)}
      </p>
      <p className="sr-only" role="status" aria-live="polite">
        {t('onboardingApply.stepOf', {
          current: index + 1,
          total: WIZARD.length,
          label: t(WIZARD[index].label),
        })}
      </p>
    </>
  )
}

/** Back and forward at the foot of a step: the primary action first on a narrow screen. */
export function WizardActions({
  onBack,
  next,
  loading,
  arrow = true,
}: {
  onBack: () => void
  next: string
  loading?: boolean
  arrow?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
      <Button type="button" variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onBack}>
        {t('onboardingApply.back')}
      </Button>
      <Button type="submit" size="lg" className="w-full sm:w-auto" loading={loading}>
        {next}
        {arrow && <RiArrowRightLine className="-mr-1 ml-2 size-5" aria-hidden="true" />}
      </Button>
    </div>
  )
}
